import React, { useCallback, useState } from 'react';
import TrueImpactText from '../../../design-system/base/TrueImpactText';
import styled from 'styled-components';
import {
  LoadFilters,
  loadFilters,
  LoadFiltersApi,
} from 'donor/portal/pages/FeedbackDashboard/tempData';
import ShowFilter, { ApplyFilters, ShowFilterProps } from './ShowFilter';
import { FeedbackDashboardAppliedFiltersProps } from '../FeedbackDashboardAppliedFilters';
import { formattedDate } from '../../../utils/date';
import TrueImpactFilterModal from '../../../design-system/components/TrueImpactFilterModal';

const isMatch = (filter: LoadFilters, searchText: string): boolean => {
  return filter.filterName.toLowerCase().includes(searchText);
};

const renderFiltersRow = (
  filters: LoadFilters[],
  filterType: ShowFilterProps['filterType'],
  searchText: string,
  handleClose: LoadAndSaveDialogProps['handleClose'],
  handleDelete: ShowFilterProps['onDelete'],
  handleSave: ShowFilterProps['onSave'],
  handleApplyAllFilters: LoadAndSaveDialogProps['handleApplyAllFilters'],
): { renderFilters: React.ReactNodeArray | null; renderedAny: boolean } => {
  let renderedAny = false;
  const renderFilters = filters.map((p, i) => {
    if (!isMatch(p, searchText)) return null;
    renderedAny = true;
    return (
      <ShowFilter
        key={p.id + p.filterName}
        {...p}
        index={i}
        filterType={filterType}
        handleDialogClose={handleClose}
        onDelete={handleDelete}
        onSave={handleSave}
        onApply={handleApplyAllFilters}
        mode="show"
      />
    );
  });
  return {
    renderFilters,
    renderedAny,
  };
};

const StyledDiv = styled.div`
  .filter-heading {
    margin-bottom: 8px;
  }
  .organization-filters {
    margin-top: 32px;
  }
`;

const renderNoMatchFound: JSX.Element = <div>No Match Found</div>;

interface LoadAndSaveDialogProps extends FeedbackDashboardAppliedFiltersProps {
  open: boolean;
  mode: 'load' | 'save';
  setMode: (mode: 'load' | 'save') => unknown;
  handleApplyAllFilters: (filters: ApplyFilters) => unknown;
  handleClose: () => unknown;
}

const LoadAndSaveDialog: React.FC<LoadAndSaveDialogProps> = ({
  open,
  mode,
  donorNames,
  relationships,
  portfolioNames,
  cycles,
  status,
  trueImpactStaff,
  dueDates,
  years,
  setMode,
  handleApplyAllFilters,
  handleClose,
}) => {
  const [text, setText] = useState<string>('');
  const [tempFilters, setTempFilters] = useState<LoadFiltersApi>(loadFilters);

  const handleDelete = useCallback(
    (index: number, filterType: keyof LoadFiltersApi) => {
      setTempFilters(s => {
        const newArray = [...s[filterType]];
        newArray.splice(index, 1);
        return { ...s, [filterType]: newArray };
      });
    },
    [setTempFilters],
  );

  const handleDeleteNewFilter = useCallback(() => {
    setMode('load');
  }, [setMode]);

  const handleSave = useCallback(
    (
      id: string | number | undefined,
      index: number,
      usersName: string,
      filterName: string,
      filterType: keyof LoadFiltersApi,
    ) => {
      setTempFilters(s => {
        const newArray = [...s[filterType]];
        newArray[index].filterName = filterName;
        return { ...s, [filterType]: newArray };
      });
    },
    [setTempFilters],
  );

  const handleSaveNew = useCallback(
    (
      id: string | number,
      index: number,
      usersName: string,
      filterName: string,
      filterType: keyof LoadFiltersApi,
    ) => {
      setMode('load');
      setTempFilters(s => {
        const filter: LoadFilters = {
          id,
          createdDate: formattedDate(new Date()) as string,
          donorNameFilters: donorNames.filters,
          relationshipFilters: relationships.filters,
          portfolioNameFilters: portfolioNames.filters,
          cyclesFilters: cycles.filters,
          statusFilters: status.filters,
          staffFilters: trueImpactStaff.filters,
          dueDateFilters: dueDates.filters,
          yearFilters: years.filters,
          usersName,
          filterName,
        };
        const newArray: LoadFilters[] = [...s[filterType]];
        newArray.unshift(filter);
        return { ...s, [filterType]: newArray };
      });
    },
    [
      donorNames,
      relationships,
      portfolioNames,
      cycles,
      status,
      trueImpactStaff,
      dueDates,
      years,
    ],
  );
  const searchText = text.toLowerCase();
  const {
    renderFilters: renderPersonalFilters,
    renderedAny: renderedAnyPersonal,
  } = renderFiltersRow(
    tempFilters.personal,
    'personal',
    searchText,
    handleClose,
    handleDelete,
    handleSave,
    handleApplyAllFilters,
  );
  const {
    renderFilters: renderOrganizationalFilters,
    renderedAny: renderedAnyOrganizational,
  } = renderFiltersRow(
    tempFilters.organizational,
    'organizational',
    searchText,
    handleClose,
    handleDelete,
    handleSave,
    handleApplyAllFilters,
  );

  return (
    <TrueImpactFilterModal
      open={open}
      heading="Saved Filters & Sort"
      searchText={searchText}
      onSearchTextChange={setText}
      onClose={handleClose}
      label="Load and Save Filters"
      description="Load and Save filters for feedback dashboard"
    >
      <StyledDiv>
        <div className="my-filters">
          <TrueImpactText
            variant="heading-subheading"
            className="filter-heading"
          >
            My Saved Filters &amp; Sorts
          </TrueImpactText>
          {mode === 'save' && (
            <ShowFilter
              mode="add"
              createdDate={formattedDate(new Date())}
              filterName=""
              usersName="you"
              index={-1}
              filterType="personal"
              handleDialogClose={handleClose}
              onDelete={handleDeleteNewFilter}
              onSave={handleSaveNew}
              onApply={handleApplyAllFilters}
            />
          )}
          {renderedAnyPersonal ? renderPersonalFilters : renderNoMatchFound}
        </div>
        <div className="organization-filters">
          <TrueImpactText
            variant="heading-subheading"
            className="filter-heading"
          >
            All True Impact Saved Filters &amp; Sorts
          </TrueImpactText>
          {renderedAnyOrganizational
            ? renderOrganizationalFilters
            : renderNoMatchFound}
        </div>
      </StyledDiv>
    </TrueImpactFilterModal>
  );
};

export default LoadAndSaveDialog;
